import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';

import { EXECUTIVE_SUMMARY_ROUTE } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { ExecutiveSummaryResponse } from 'types/landing';
import { getConfig } from 'utils/helperMethods';

/**
 * Simulated round-trip so the loading state is exercised in development rather than only
 * appearing the day the endpoint is wired up.
 */
const MOCK_LATENCY_MS = 600;

/**
 * Executive Summary KPIs for the landing page.
 *
 * Deliberately a real `useQuery` over a `demoConfig.executiveSummary` fixture rather than a
 * plain constant, so the component already handles the full loading / error / empty lifecycle.
 * The fixture is shaped exactly like the future BFF response (docs/api/landing-bff-contract.md);
 * swapping to the BFF is a three-line change inside `queryFn` — see the TODO below.
 */
export function useExecutiveSummary() {
    const { appConfig } = useContext(UserContext);
    const tiles = getConfig('demoConfig', appConfig)?.executiveSummary?.tiles ?? [];

    const { data, isLoading, isError } = useQuery({
        queryKey: ['rgm-executive-summary', tiles],
        queryFn: async (): Promise<ExecutiveSummaryResponse> => {
            // TODO(BFF): replace this block with
            //   return (await axios.get(landingUrls.getExecutiveSummaryUrl)).data;
            await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
            return { detail_route: EXECUTIVE_SUMMARY_ROUTE, cards: tiles };
        },
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
    });

    return {
        cards: data?.cards ?? [],
        detailRoute: data?.detail_route ?? EXECUTIVE_SUMMARY_ROUTE,
        isLoading,
        isError,
    };
}

export default useExecutiveSummary;
